import { uniq } from "lodash";
import type {
  DecisionEngineChannelConfig,
  DecisionEngineChannelType,
  DecisionEnginePreviewRecipientMode,
  VariableDB,
} from "@hightouch/lib/customer-data/decision-engine/types";
import {
  generateSyncConfigFromSpec,
  getDestinationDefinition,
} from "@hightouch/lib/customer-data/decision-engine/destination-definitions";
import type {
  DestinationType,
  MessageTagMap,
} from "@hightouch/lib/customer-data/decision-engine/destination-definitions/sync-config-helpers";

// Re-export from the lib layer for backward compatibility
export {
  generateDataExtensionKey,
  hightouchMapping,
  hightouchMappingWithTags,
  getMappingForVariable,
  getMappingForUserColumn,
} from "@hightouch/lib/customer-data/decision-engine/destination-definitions/sync-config-helpers";
export type {
  DestinationType,
  MessageTagMap,
} from "@hightouch/lib/customer-data/decision-engine/destination-definitions/sync-config-helpers";

export const DECISION_ENGINE_WAREHOUSE_HISTORY_CONFIG = {
  tablesToWrite: { changelog: true, snapshot: true, syncRuns: true },
};

export const generateDecisionEngineSyncConfig = ({
  flowId,
  channelId,
  destinationType,
  channelConfig,
  isPreview,
  sendToHightouchUser,
  previewRecipientMode,
  channelType,
  campaignId,
  resourceId,
  messageVariables = [],
  messageTags = {},
}: {
  flowId: string;
  channelId: string;
  destinationType: DestinationType;
  channelType: DecisionEngineChannelType;
  channelConfig: DecisionEngineChannelConfig;
  isPreview: boolean;
  sendToHightouchUser: boolean;
  previewRecipientMode?: DecisionEnginePreviewRecipientMode;
  campaignId?: string | number;
  resourceId?: string | number;
  messageVariables?: VariableDB[];
  messageTags?: MessageTagMap;
}): Record<string, any> => {
  const userColumns = (channelConfig.userColumns ?? []).filter(
    (column) =>
      !messageVariables?.some(
        (variable) => variable.name === column.column.toLowerCase(),
      ),
  );

  return generateSyncConfigFromSpec(destinationType, {
    producer: "aid",
    scopeId: flowId,
    unitId: channelId,
    channelType,
    settings: { ...channelConfig, userColumns },
    campaignId,
    syncConfigOverride: channelConfig.syncConfigOverride,
    syncConfigAppend: channelConfig.syncConfigAppend,
    isPreview,
    sendToHightouchUser,
    previewRecipientMode,
    resourceId,
    messageVariables,
    messageTags,
  });
};

export const getDecisionEngineSyncSchedule = ({
  channelConfig,
  destinationType,
  schedulingTimezone,
}: {
  channelConfig: DecisionEngineChannelConfig;
  destinationType: DestinationType;
  schedulingTimezone: string | null;
}) => {
  if (channelConfig.syncScheduleOverride) {
    return {
      type: "cron",
      schedule: { expression: channelConfig.syncScheduleOverride },
    };
  }
  const definition = getDestinationDefinition(destinationType, channelConfig);
  const customSchedule = definition.syncStrategy?.getSyncSchedule;
  if (customSchedule) {
    return customSchedule({ channelConfig, schedulingTimezone });
  }
  return { type: "cron", schedule: { expression: "0 * * * *" } };
};

/**
 * Grab the tag schema from message tags
 * Once we migrate to fully using the decision engine config for the schema,
 * we can remove this function.
 */
export function getTagSchemaFromTags(tags: Record<string, string>[]): {
  [key: string]: Array<any>;
} {
  const result: { [key: string]: any[] } = tags.reduce(
    (acc, obj) => {
      Object.keys(obj).forEach((key) => {
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(obj[key]);
      });
      return acc;
    },
    {} as { [key: string]: any[] },
  );

  Object.keys(result).forEach((key) => {
    result[key] = uniq(result[key]);
  });

  return result;
}
